import React, { useCallback, useMemo } from 'react';
import { InputAdornment, Stack } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Button,
  Switch,
  SwitchLabel,
  TextField,
} from '../design-system';

const filterLabelStyles = { flexShrink: 0 };
const filterLabelSlotProps = { typography: { fontSize: 12, fontWeight: 500 } };
const filterInputStyles = {
  flex: 1,
  minWidth: 0,
  '& .MuiInputBase-root': { minHeight: 34, height: 34, fontSize: 14, pr: 0.75 },
  '& .MuiInputBase-input': {
    fontSize: 12,
    height: 34,
    boxSizing: 'border-box',
    padding: '4px 2px 4px 8px',
  },
  '& .MuiInputAdornment-root': { ml: 0 },
};
const clearIconStyles = { fontSize: 14 };
const clearIconActiveStyles = { fontSize: 14, cursor: 'pointer' };

interface SiteListControlsProps {
  nameFilter: string;
  setNameFilter: (value: string) => void;
  onlyOwned: boolean;
  setOnlyOwned: (value: boolean) => void;
}

const SiteListControls = ({
  nameFilter,
  setNameFilter,
  onlyOwned,
  setOnlyOwned,
}: SiteListControlsProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const handleCreate = useCallback(() => {
    navigate('/s/create');
  }, [navigate]);

  const handleShowAll = useCallback(() => {
    navigate('/s');
  }, [navigate]);

  const handleClickOnlyOwned = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setOnlyOwned(event.target.checked);
    },
    [setOnlyOwned]
  );

  const handleNameFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNameFilter(event.target.value);
    },
    [setNameFilter]
  );

  const handleClearNameFilter = useCallback(() => {
    setNameFilter('');
  }, [setNameFilter]);

  const filterInputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          {nameFilter ? (
            <ClearIcon
              sx={clearIconActiveStyles}
              onClick={handleClearNameFilter}
            />
          ) : (
            <ClearIcon sx={clearIconStyles} htmlColor="transparent" />
          )}
        </InputAdornment>
      ),
    }),
    [handleClearNameFilter, nameFilter]
  );

  return (
    <Stack>
      <Stack mb={2} direction="row" justifyContent="space-between">
        <Button
          onClick={handleCreate}
          startIcon={<AddCircleIcon />}
        >
          <FormattedMessage id="map.site.create" defaultMessage="Create" />
        </Button>
        <Button
          onClick={handleShowAll}
          tone="neutral"
        >
          <FormattedMessage id="map.site.showAll" defaultMessage="Show all" />
        </Button>
      </Stack>
      <Stack
        mb={1.5}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <TextField
          surface="sunken"
          placeholder={intl.formatMessage({
            id: 'map.site.filter',
            defaultMessage: 'Filter',
          })}
          value={nameFilter}
          onChange={handleNameFilterChange}
          sx={filterInputStyles}
          InputProps={filterInputProps}
        />
        <SwitchLabel
          sx={filterLabelStyles}
          control={
            <Switch
              checked={onlyOwned}
              onChange={handleClickOnlyOwned}
              tone="primary"
            />
          }
          label={
            <FormattedMessage id="map.site.owned" defaultMessage="Owned" />
          }
          labelPlacement="start"
          slotProps={filterLabelSlotProps}
        />
      </Stack>
    </Stack>
  );
};

export { SiteListControls };
